/**
 * oDIN Living Oracle, Phase 4 v1.3 — integrated into Midgard.
 * Art registered to 1120×510 scenic window (source-first recovery).
 * Host (OdinAIPage) supplies real conversations, SSE stream, auth.
 */
import React, { useEffect, useRef, useState } from 'react';
import './OdinLivingWidget.css';
import OdinMarkdown, { plainText } from './OdinMarkdown';
import OdinDisclosure from './OdinDisclosure';
import figure from '../../assets/odin/odin-figure.webp';
import face from '../../assets/odin/odin-face.webp';
import halo from '../../assets/odin/wisdom-halo.webp';
import ravenLeft from '../../assets/odin/huginn.webp';
import ravenLeftWing from '../../assets/odin/huginn-wing.webp';
import ravenRight from '../../assets/odin/muninn.webp';
import ravenRightWing from '../../assets/odin/muninn-wing.webp';
import eye from '../../assets/odin/eye-highlight.webp';
import patch from '../../assets/odin/eyepatch-gem.webp';
import flameLeft from '../../assets/odin/left-brazier.webp';
import flameRight from '../../assets/odin/right-brazier.webp';
import depth from '../../assets/odin/sanctum-depth.webp';
import canonLogo from '../../assets/odin/Midgard_OFFICIAL_Approved_M_Logo_transparent.png';

const nav = [
  ['Chronicles', '▤'], ['Seek', '⌕'], ['The Forge', '⚒'],
  ['The Vault', '▣'], ['Visions', '◇'], ['Moving Visions', '▷'],
];
const cardPrompts = [
  ['Explore lore','Tell me about the Nine Realms.','◈'],
  ['Seek guidance','Help me decide what to explore in Midgard.','✧'],
  ['Understand','Explain Yggdrasil in simple terms.','◎'],
  ['Create','Help me draft a Norse-inspired saga.','✦'],
];

const NARROW_QUERY = '(max-width: 600px)';
function useNarrowView() {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(NARROW_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(NARROW_QUERY);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return narrow;
}

/** Phase 4 v1.3: art registered to approved 1120×510 scenic window. */
export function OdinSpiritScene({ speaking=false, listening=false, compact=false }) {
  const [pointer,setPointer]=useState({x:0,y:0});
  const move = (e) => {
    if(e.pointerType !== 'mouse') return;
    const r=e.currentTarget.getBoundingClientRect();
    const x=((e.clientX-r.left)/r.width-.5)*2, y=((e.clientY-r.top)/r.height-.5)*2;
    if(Math.abs(x-pointer.x)>.08 || Math.abs(y-pointer.y)>.08) setPointer({x,y});
  };
  // Phones: frame the same scene from x 130–990 (oDIN, both ravens with their
  // nameplates, the emblem) so it renders ~30% larger. Artwork and layers unchanged.
  const narrow = useNarrowView();
  const art=(src,x,y,w,h,cls)=> <image href={src} x={x} y={y} width={w} height={h} preserveAspectRatio="none" className={cls||''} />;
  return <div className={'odin-spirit-scene odin-registered-scene'+(compact?' odin-scene-compact':'')+(speaking?' is-speaking':'')+(listening?' is-listening':'')} onPointerMove={move} onPointerLeave={()=>setPointer({x:0,y:0})}>
    <svg className="odin-registered-canvas" viewBox={narrow ? '130 0 860 510' : '0 0 1120 510'} preserveAspectRatio="xMidYMin meet" role="img" aria-label="oDIN within the Norse oracle sanctum, with Huginn and Muninn">
      {art(depth,0,0,1120,510,'odin-registered-backplate')}
      <g className="odin-registered-halo">{art(halo,283,-1,543,120,'odin-registered-halo-img')}</g>
      <g className="odin-registered-body" style={{'--x':`${pointer.x*1.2}px`,'--y':`${pointer.y*0.9}px`}}>
        {art(figure,116,30,849,454,'odin-registered-figure')}
        {art(eye,505,153,41,30,'odin-registered-eye')}
        {art(patch,543,109,48,74,'odin-registered-patch')}
        <circle cx="570" cy="424" r="56" fill="#170d23" opacity=".98" stroke="#713cc0" strokeWidth="1.7" />
        {art(canonLogo,510,364,120,120,'odin-registered-sacred-logo')}
      </g>
      <g className="odin-registered-huginn" style={{'--x':`${pointer.x*1.7}px`,'--y':`${pointer.y*1.1}px`}}>
        {/* "HUGINN / THOUGHT" lives in the wing sprite; backplate has an inpaint patch there, so show that region at rest. */}
        <defs>
          <filter id="odin-huginn-label-soft" filterUnits="userSpaceOnUse" x="110" y="275" width="190" height="155"><feGaussianBlur stdDeviation="6"/></filter>
          <mask id="odin-huginn-label-mask" maskUnits="userSpaceOnUse" x="103" y="233" width="183" height="161"><rect x="140" y="305" width="128" height="93" fill="#fff" filter="url(#odin-huginn-label-soft)"/></mask>
        </defs>
        <g mask="url(#odin-huginn-label-mask)">{art(ravenLeftWing,103,233,183,161,'odin-registered-label')}</g>
        {art(ravenLeft,104,160,251,241,'odin-registered-raven')}
        {art(ravenLeftWing,103,233,183,161,'odin-registered-flap left')}
      </g>
      <g className="odin-registered-muninn" style={{'--x':`${pointer.x*1.7}px`,'--y':`${pointer.y*1.1}px`}}>
        {art(ravenRight,780,168,226,219,'odin-registered-raven')}
        {art(ravenRightWing,866,225,139,159,'odin-registered-flap right')}
      </g>
      <g className="odin-registered-fire">
        {art(flameLeft,25,392,80,141,'odin-registered-fire-left')}
        {art(flameRight,1041,397,78,138,'odin-registered-fire-right')}
      </g>
      <g className="odin-registered-ambient" aria-hidden="true">
        <ellipse cx="567" cy="52" rx="208" ry="22" fill="#a66aff" opacity=".065"/>
      </g>
    </svg>
    <div className="odin-scene-dim"/>
  </div>;
}

function Icons({type}) {
  const paths={
    menu:<><path d="M4 7h16M4 12h16M4 17h16"/></>,
    close:<><path d="M5 5l14 14M19 5L5 19"/></>,
    plus:<><path d="M12 5v14M5 12h14"/></>,
    send:<><path d="m4 12 16-8-6 16-3-7-7-1ZM11 13l9-9"/></>,
    search:<><circle cx="10.8" cy="10.8" r="6.8"/><path d="m16 16 5 5"/></>,
    volume:<><path d="M4 9v6h4l5 4V5L8 9H4ZM17 9q3 3 0 6"/></>,
    mute:<><path d="M4 9v6h4l5 4V5L8 9H4ZM17 9l5 6M22 9l-5 6"/></>,
    hide:<><path d="m15 6-6 6 6 6"/></>,
    book:<><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></>,
  };
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" width="19" height="19" aria-hidden="true">{paths[type]||paths.menu}</svg>;
}

const short = s => (s||'Untitled Chronicle').slice(0,48);

export default function OdinLivingWidget({
  conversations=[], messages=[], activeConversationId=null,
  onCreateConversation, onSelectConversation, onSendMessage,
  onOpenLibrary, onAttachFiles, onOpenWorkspace,
  busy=false, streamState='idle', hourlyRemaining=null,
  disclosureOpen=false, onDisclosureClose, onDisclosureOpen,
  initialChroniclesOpen,
  greeting='Speak your question, wanderer.',
}) {
  const [chroniclesOpen,setChroniclesOpen]=useState(()=>initialChroniclesOpen ?? (typeof window === 'undefined' || window.innerWidth>=900));
  const [query,setQuery]=useState('');
  const [draft,setDraft]=useState('');
  const [openAttach,setOpenAttach]=useState(false);
  const [previewFiles,setPreviewFiles]=useState([]);
  const [selectedNav,setSelectedNav]=useState('Chronicles');
  const [navCollapsed,setNavCollapsed]=useState(true);
  const [readAloud,setReadAloud]=useState(false);
  const fileInput=useRef(); const messagesEnd=useRef(null);
  const hasChat=!!activeConversationId || messages.length>0;
  useEffect(()=>{messagesEnd.current?.scrollIntoView?.({block:'end',behavior:'smooth'});},[messages.length]);
  useEffect(()=>()=>{if(typeof window!=='undefined' && 'speechSynthesis' in window) window.speechSynthesis.cancel();},[]);
  const filtered=conversations.filter(c=>short(c.title).toLowerCase().includes(query.toLowerCase()));
  const speak = (content)=>{
    if(typeof window==='undefined'||!('speechSynthesis' in window))return;
    window.speechSynthesis.cancel();
    if(readAloud){setReadAloud(false);return;}
    const utterance=new SpeechSynthesisUtterance(content);utterance.onend=()=>setReadAloud(false);utterance.onerror=()=>setReadAloud(false);
    window.speechSynthesis.speak(utterance);setReadAloud(true);
  };
  const submit = async (value=draft)=>{
    if(!value.trim()||busy)return;
    if(previewFiles.length>0)return; /* multimodal not yet active */
    setDraft(''); await onSendMessage?.(value.trim());
  };
  const selectFile = (files)=>{
    const list=[...files].filter(f=>f.type.startsWith('image/')||f.type.startsWith('video/')).slice(0,4);
    setPreviewFiles(list);setOpenAttach(false);
    if(onAttachFiles && list.length)onAttachFiles(list);
  };
  const begin = async ()=>{
    setDraft('');setPreviewFiles([]);
    await onCreateConversation?.();
    setSelectedNav('Chronicles');
  };
  const toggleRecords = () => setChroniclesOpen(v=>!v);

  return <section className={'odin-widget'+(chroniclesOpen?' has-chronicles':'')} aria-label="oDIN Oracle workspace">
    <main className="odin-main">
      <div className="odin-stage">
        <OdinSpiritScene speaking={streamState==='speaking'} listening={busy}/>

        {/* ── Floating Records toggle — visible on welcome screen (fix 2/5) ── */}
        {!hasChat && (
          <button className="odin-records-float" type="button"
            onClick={toggleRecords}
            aria-expanded={chroniclesOpen}
            aria-label={chroniclesOpen?'Hide Records':'Show Records'}
            title={chroniclesOpen?'Hide Records':'Show Records'}>
            <Icons type={chroniclesOpen?'hide':'book'}/>
            <span>Records</span>
          </button>
        )}

        {hasChat ? (
          <div className="odin-conversation" aria-label="Conversation messages">
            {/* ── Companion heading — the only oDIN header (fix 2) ── */}
            <div className="odin-conversation-heading">
              <div className="odin-avatar"><img src={face} alt="oDIN companion"/></div>
              <div>
                <strong>oDIN</strong>
                <small>{busy?'Consulting the runes…':'Your guide is present'}</small>
              </div>
              <button type="button"
                onClick={()=>speak(plainText(messages.filter(m=>m.role==='assistant').at(-1)?.content||''))}
                disabled={!messages.some(m=>m.role==='assistant')}
                aria-label={readAloud?'Stop reading':'Read the last oDIN answer aloud'}
                title="Read aloud">
                <Icons type={readAloud?'mute':'volume'}/>
              </button>
              {/* Records toggle inside conversation heading (fix 2/5) */}
              <button type="button" className="odin-head-records"
                onClick={toggleRecords}
                aria-expanded={chroniclesOpen}
                aria-label={chroniclesOpen?'Hide Records':'Show Records'}
                title={chroniclesOpen?'Hide Records':'Show Records'}>
                <Icons type={chroniclesOpen?'hide':'book'}/>
                <span className="odin-head-records-text">Records</span>
              </button>
            </div>

            {/* Message scroll — sanctum stays visible behind messages (fix 3) */}
            <div className="odin-message-scroll" role="log" aria-live="polite">
              {messages.map((m,i)=>(
                <div className={'odin-message odin-'+(m.role==='user'?'mine':'reply')} key={m.id||i}>
                  {m.role==='user'?<span>{m.content}</span>:<OdinMarkdown text={m.content}/>}
                  {m.role==='assistant'&&(
                    <button type="button" title="Read aloud" aria-label="Read message aloud"
                      onClick={()=>speak(plainText(m.content))}>
                      <Icons type="volume"/>
                    </button>
                  )}
                </div>
              ))}
              {busy&&<div className="odin-thinking">The runes stir…</div>}
              <div ref={messagesEnd}/>
            </div>
          </div>
        ) : (
          <div className="odin-welcome">
            <div className="odin-inscription">
              <p className="odin-overline">The Allfather's Echo</p>
              <h1>oDIN</h1>
              <p className="odin-welcome-text">{greeting}</p>
              <p className="odin-welcome-sub">The knowledge of Midgard's realms, lore and people flows through me.</p>
            </div>
            <div className="odin-prompts">
              {cardPrompts.map(([title,prompt,symbol])=>(
                <button key={title} type="button" onClick={()=>setDraft(prompt)}>
                  <span>{symbol}</span><strong>{title}</strong>
                  <small>Ask oDIN <span aria-hidden="true">↗</span></small>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Composer ── */}
      <div className="odin-composer-wrap">
        {disclosureOpen&&<OdinDisclosure onClose={onDisclosureClose}/>}
        <div className="odin-composer-meta">
          <span className="odin-premium-lock" title="Future subscription feature">◇ Premium models · Coming Soon</span>
          <span className="odin-meta-right">
            {hourlyRemaining!==null&&<span className="odin-quota" title="Time, Runegold balance, weather and other direct lookups do not count">{hourlyRemaining} AI replies left this hour</span>}
            {onDisclosureOpen&&!disclosureOpen&&(
              <button type="button" className="odin-info-btn" onClick={onDisclosureOpen}
                aria-label="How oDIN processes your messages">ⓘ AI &amp; privacy</button>
            )}
          </span>
        </div>
        {previewFiles.length>0&&(
          <div className="odin-attachment-preview">
            {previewFiles.map((f,i)=>(
              <span key={i}>📎 {f.name}{' '}
                <button type="button" onClick={()=>setPreviewFiles(arr=>arr.filter((_,j)=>j!==i))} aria-label={'Remove '+f.name}>×</button>
              </span>
            ))}
            <small>Media analysis is not active yet. Remove attachments to send text.</small>
          </div>
        )}
        <form className="odin-composer" onSubmit={e=>{e.preventDefault();submit();}}>
          <div className="odin-attach">
            <button type="button" aria-label="Add attachments" aria-expanded={openAttach} onClick={()=>setOpenAttach(v=>!v)}>
              <Icons type="plus"/>
            </button>
            {openAttach&&(
              <div className="odin-attach-menu" role="menu">
                <button type="button" role="menuitem" onClick={()=>fileInput.current?.click()}>Upload photo or video</button>
                <button type="button" role="menuitem" onClick={()=>{setOpenAttach(false);onOpenLibrary?.();}}>Open The Vault</button>
                <small>Files are previewed only until multimodal analysis is enabled.</small>
              </div>
            )}
          </div>
          <input ref={fileInput} hidden type="file" accept="image/*,video/*" multiple onChange={e=>{selectFile(e.target.files||[]);e.target.value='';}}/>
          <label htmlFor="odin-input-field" className="odin-visually-hidden">Ask oDIN</label>
          <input id="odin-input-field" value={draft} onChange={e=>setDraft(e.target.value)} placeholder="Ask oDIN anything…" maxLength={8000}/>
          <button type="button"
            onClick={()=>speak(plainText(messages.filter(m=>m.role==='assistant').at(-1)?.content||''))}
            aria-label={readAloud?'Stop speech':'Read last reply aloud'}
            title="Speech synthesis"
            disabled={!messages.some(m=>m.role==='assistant')}>
            <Icons type="volume"/>
          </button>
          <button className="odin-send" type="submit" disabled={busy||!draft.trim()||!!previewFiles.length} aria-label="Send message">
            <Icons type="send"/>
          </button>
        </form>
      </div>
    </main>

    {/* ── Records sidebar (fix 4/5) ── */}
    <aside className={'odin-chronicles'+(chroniclesOpen?'':' odin-closed')} aria-label="oDIN workspace — Records" aria-hidden={!chroniclesOpen}>
      <div className="odin-side-title">
        <span>THE ORACLE'S RECORDS</span>
        <button type="button" aria-label="Close Records" onClick={()=>setChroniclesOpen(false)}>
          <Icons type="close"/>
        </button>
      </div>
      <button className="odin-begin" onClick={begin} type="button">
        <Icons type="plus"/> Begin Chronicle
      </button>
      <div className="odin-nav-toggle">
        <button type="button" onClick={()=>setNavCollapsed(v=>!v)}
          aria-expanded={!navCollapsed} title={navCollapsed?'Show workspace links':'Hide workspace links'}>
          <span>WORKSPACE</span><span aria-hidden="true">{navCollapsed?'▼':'▲'}</span>
        </button>
      </div>
      {!navCollapsed&&(
        <div className="odin-workspace-nav odin-workspace-scroll">
          {nav.map(([name,icon])=>(
            <button type="button" key={name} className={selectedNav===name?'active':''}
              onClick={()=>{setSelectedNav(name);if(['Chronicles','Seek'].includes(name))return;onOpenWorkspace?.(name);}}>
              <span>{icon}</span> {name}
              {['The Forge','Visions','Moving Visions'].includes(name)&&<small>Soon</small>}
            </button>
          ))}
        </div>
      )}
      {(selectedNav==='Seek'||selectedNav==='Chronicles') ? (
        <>
          <label className="odin-search">
            <Icons type="search"/>
            <input placeholder="Search Chronicles..." value={query} onChange={e=>setQuery(e.target.value)}/>
          </label>
          <p className="odin-list-caption">SESSIONS · {filtered.length}</p>
          <div className="odin-session-list">
            {filtered.length===0
              ? <p className="odin-no-results">No matching chronicles</p>
              : filtered.map(c=>(
                  <button className={activeConversationId===c.id?'active':''} type="button" key={c.id}
                    onClick={()=>onSelectConversation?.(c.id)}>
                    <span>▤</span><span>{short(c.title)}</span>
                  </button>
                ))
            }
          </div>
        </>
      ) : (
        <div className="odin-side-placeholder">
          {selectedNav==='The Vault'?'Access your saved files through Midgard\'s existing library.':'Future workspace — not active yet.'}
        </div>
      )}
      <div className="odin-side-footer"><img src={canonLogo} alt=""/> MIDGARD · oDIN</div>
    </aside>
  </section>;
}
